import { z } from 'zod';

/** 25 MiB: the storage module's presigned-POST policy (`max_upload_bytes`, ADR-0015 §2). */
export const ATTACHMENT_MAX_BYTES = 26_214_400;
/** Files per ticket create / comment. */
export const ATTACHMENT_MAX_PER_REQUEST = 20;
export const ATTACHMENT_FILENAME_MAX = 255;
export const ATTACHMENT_TOO_LARGE = 'attachment.too_large';
export const ATTACHMENT_CONTENT_TYPE_NOT_ALLOWED = 'attachment.content_type_not_allowed';

export const ATTACHMENT_STATUSES = ['pending', 'ready'] as const;
export const AttachmentStatusSchema = z.enum(ATTACHMENT_STATUSES);
export type AttachmentStatus = z.infer<typeof AttachmentStatusSchema>;

/** Common document, image, archive, text and mail types; no executables, no wildcard. */
export const ALLOWED_CONTENT_TYPES = [
  // documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/rtf',
  // images
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/tiff',
  'image/heic',
  // archives
  'application/zip',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-tar',
  'application/x-bzip2',
  // text and data
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/calendar',
  'application/json',
  'application/xml',
  'text/xml',
  // mail
  'message/rfc822',
  'application/vnd.ms-outlook',
] as const;
export type AllowedContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

const allowed: ReadonlySet<string> = new Set(ALLOWED_CONTENT_TYPES);

/** Lower-cases and strips parameters (`text/plain; charset=utf-8` → `text/plain`). */
export const normalizeContentType = (value: string): string =>
  (value.split(';')[0] ?? '').trim().toLowerCase();

export const isAllowedContentType = (value: string): value is AllowedContentType =>
  allowed.has(normalizeContentType(value));

export const ContentTypeSchema = z
  .string()
  .max(255)
  .transform(normalizeContentType)
  .pipe(z.enum(ALLOWED_CONTENT_TYPES, { error: ATTACHMENT_CONTENT_TYPE_NOT_ALLOWED }));

const hasPathSeparatorOrControl = (name: string): boolean => {
  for (const ch of name) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === '/' || ch === '\\' || code < 0x20 || code === 0x7f) {
      return true;
    }
  }
  return false;
};

/** No path separators or control characters; the S3 key never uses the filename. */
export const AttachmentFilenameSchema = z
  .string()
  .trim()
  .min(1)
  .max(ATTACHMENT_FILENAME_MAX)
  .refine((name) => !hasPathSeparatorOrControl(name), {
    message: 'filename must not contain path separators or control characters',
  });

/**
 * `POST /tickets/{id}/attachments` (S-05-09). The 25 MiB ceiling is enforced by the API as
 * 413 `attachment.too_large` (not as a 400 here) so the story's status code holds.
 */
export const CreateAttachmentSchema = z.strictObject({
  filename: AttachmentFilenameSchema,
  contentType: ContentTypeSchema,
  sizeBytes: z.int().min(1),
  commentId: z.uuid().nullable().optional(),
});
export type CreateAttachment = z.infer<typeof CreateAttachmentSchema>;
export type CreateAttachmentInput = z.input<typeof CreateAttachmentSchema>;

export const isAttachmentTooLarge = (sizeBytes: number): boolean =>
  sizeBytes > ATTACHMENT_MAX_BYTES;

export const AttachmentSchema = z.object({
  id: z.uuid(),
  ticketId: z.uuid(),
  commentId: z.uuid().nullable(),
  uploaderId: z.uuid(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.int().min(0),
  status: AttachmentStatusSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/** 201 body of the attachment create: the pending row id plus the S3 presigned POST. */
export const PresignedUploadSchema = z.object({
  attachmentId: z.uuid(),
  upload: z.object({
    url: z.url(),
    fields: z.record(z.string(), z.string()),
  }),
});
export type PresignedUpload = z.infer<typeof PresignedUploadSchema>;

/** `tenants/<tenantId>/tickets/<ticketId>/<attachmentId>` (S-05-09). */
export const attachmentS3Key = (tenantId: string, ticketId: string, attachmentId: string) =>
  `tenants/${tenantId}/tickets/${ticketId}/${attachmentId}`;
